import React, { useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { GTFonts, useTheme, type Theme } from '../../../theme';
import { fs, formatMoney, s, v } from '../../../utils/libs';
import { formatSharePercent } from '../../../utils/paymentBreakdown';
import type { SpendingPace } from '../../../utils/expensePace';

type Props = {
  pace: SpendingPace;
  currencyText: string;
  /** e.g. "October", as in "Projected for October". */
  monthLabel: string;
};

/** Daily average so far and the month it points to (This Month only). */
const ExpensePaceCard: React.FC<Props> = ({ pace, currencyText, monthLabel }) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const change = pace.changeVsPrevious;

  return (
    <View style={styles.card}>
      <Text style={styles.title}>Spending pace</Text>
      <View style={styles.tiles}>
        <View style={styles.tile}>
          <Text style={styles.tileLabel}>Daily average</Text>
          <Text style={styles.tileValue} numberOfLines={1} adjustsFontSizeToFit>
            {currencyText} {formatMoney(pace.dailyAverage)}
          </Text>
          <Text style={styles.tileCaption}>
            Over {pace.days} {pace.days === 1 ? 'day' : 'days'}
          </Text>
        </View>
        <View style={styles.tile}>
          <Text style={styles.tileLabel}>Projected for {monthLabel}</Text>
          <Text style={styles.tileValue} numberOfLines={1} adjustsFontSizeToFit>
            {currencyText} {formatMoney(pace.projected)}
          </Text>
          {change !== null ? (
            <Text style={styles.tileCaption}>
              {/* Spending less than last month is the good direction. */}
              <Text
                style={[
                  styles.change,
                  {
                    color:
                      change > 0 ? theme.colors.error : theme.colors.success,
                  },
                ]}>
                {change > 0 ? '+' : change < 0 ? '-' : ''}
                {formatSharePercent(Math.abs(change))}
              </Text>{' '}
              vs last month
            </Text>
          ) : null}
        </View>
      </View>
    </View>
  );
};

export default ExpensePaceCard;

const createStyles = (theme: Theme) =>
  StyleSheet.create({
    card: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.md,
      paddingHorizontal: s(16),
      paddingTop: v(14),
      paddingBottom: v(16),
      rowGap: v(12),
      ...theme.shadows.sm,
    },
    title: {
      color: theme.colors.text,
      fontFamily: GTFonts.bold,
      fontSize: fs(16),
      lineHeight: fs(20),
    },
    tiles: {
      flexDirection: 'row',
      columnGap: s(10),
    },
    // Same tile as the Customer Details summary
    tile: {
      flex: 1,
      minWidth: 0,
      rowGap: v(4),
      paddingVertical: v(10),
      paddingHorizontal: s(12),
      borderRadius: theme.borderRadius.md,
      borderWidth: 1,
      borderColor: theme.colors.borderSecondary,
      backgroundColor: theme.colors.surfaceSecondary,
    },
    tileLabel: {
      color: theme.colors.textSecondary,
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(16),
    },
    tileValue: {
      color: theme.colors.text,
      fontFamily: GTFonts.semibold,
      fontSize: fs(16),
      lineHeight: fs(20),
    },
    tileCaption: {
      color: theme.colors.textSecondary,
      fontFamily: GTFonts.regular,
      fontSize: fs(11),
      lineHeight: fs(14),
    },
    change: {
      fontFamily: GTFonts.semibold,
    },
  });
